import React from 'react';
import styled from 'styled-components';
import { ReactComponent as IncidentIoIcon } from '@elementary/common-ui/assets/16x16/incident-io.svg';
import { ReactComponent as RootlyIcon } from '@elementary/common-ui/assets/16x16/rootly.svg';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/16x16/opsgenie.svg';
import { ReactComponent as PagerIcon } from '@elementary/common-ui/assets/16x16/pager.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '@elementary/common-ui/assets/16x16/teams.svg';
import { ReactComponent as EmailIcon } from '@elementary/common-ui/assets/16x16/email.svg';
import { Flex, FlexCenter } from '@elementary/common-ui/components';
import { Box, Tooltip, Typography } from '@mui/material';
import { AlertDestination, IntegrationType } from 'shared/types';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { fontSize, fontWeight, space } from '@elementary/common-ui/style/utils';

const MAX_NODES = 1;

const channelIconMap: Record<IntegrationType, JSX.Element> = {
  slack: <SlackIcon />,
  pager_duty: <PagerIcon />,
  microsoft_teams: <TeamsIcon />,
  microsoft_teams_bot: <TeamsIcon />,
  opsgenie: <OpsgenieIcon />,
  incident_io: <IncidentIoIcon />,
  rootly: <RootlyIcon />,
  email: <EmailIcon />,
};

const Wrapper = styled(Box)<{ $muted: boolean }>`
  ${({ $muted }) =>
    $muted &&
    `
    opacity: 0.3;
  `}
  transition: opacity 0.2s ease-in-out;
`;

const TooltipContentWrapper = styled(Flex)`
  width: fit-content;
  max-width: 240px;
  flex-wrap: wrap;
  gap: ${space('sm')};
  > .MuiTypography-root {
    color: ${({ theme }) => theme.palette.content.inverse};
  }
`;

const Label = styled(Typography)`
  font-size: ${fontSize('base')};
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type DestinationItemProps = {
  type?: AlertIntegrationType;
  channel: string;
  className?: string;
};

const DestinationItem = styled(
  React.forwardRef<HTMLDivElement, DestinationItemProps>(
    ({ type, channel, className, ...props }, ref) => {
      let displayChannel = channel;
      if (type === 'EMAIL') {
        try {
          const parsed = JSON.parse(channel);
          if (parsed.to && Array.isArray(parsed.to)) {
            displayChannel = parsed.to.join(', ');
          }
        } catch {
          displayChannel = channel;
        }
      }

      return (
        <Flex ref={ref} className={className} {...props}>
          <FlexCenter>
            {type && channelIconMap[type.toLowerCase() as IntegrationType]}
          </FlexCenter>
          <Label>{displayChannel}</Label>
        </Flex>
      );
    },
  ),
)`
  gap: ${space('sm')};
  align-items: center;
  overflow: hidden;
  min-width: 0;
`;

type Props = {
  alertDestinations?: AlertDestination[];
  muted: boolean;
};

const AlertDestinationsTableCell = ({
  alertDestinations = [],
  muted,
}: Props) => (
  <Tooltip title={muted && 'Alerts are disabled'}>
    <Wrapper $muted={muted}>
      {alertDestinations.length > 0 ? (
        <Flex
          style={{
            gap: themeTokens.space.sm,
            alignItems: 'center',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {alertDestinations
            .slice(0, MAX_NODES)
            ?.map((destination: AlertDestination, index) => (
              <Tooltip
                key={index}
                title={
                  <DestinationItem
                    channel={destination.channel}
                    type={destination.type}
                  />
                }
              >
                <span style={{ overflow: 'hidden', minWidth: 0 }}>
                  <DestinationItem
                    channel={destination.channel}
                    type={destination.type}
                  />
                </span>
              </Tooltip>
            ))}
          <Flex
            sx={{ color: 'content.primary', fontWeight: fontWeight('medium') }}
          >
            {alertDestinations && alertDestinations?.length > MAX_NODES && (
              <Tooltip
                title={
                  !muted ? (
                    <TooltipContentWrapper>
                      {alertDestinations
                        .slice(MAX_NODES)
                        .map((destination, index) => (
                          <React.Fragment key={index}>
                            {index !== 0 && <Typography>,</Typography>}{' '}
                            <DestinationItem
                              channel={destination.channel}
                              type={destination.type}
                            />
                          </React.Fragment>
                        ))}
                    </TooltipContentWrapper>
                  ) : (
                    ''
                  )
                }
              >
                <span>
                  <Typography>{`+${
                    alertDestinations.length - MAX_NODES
                  }`}</Typography>
                </span>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ) : (
        ''
      )}
    </Wrapper>
  </Tooltip>
);

export default AlertDestinationsTableCell;
